import { darken } from 'polished';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { SebBuild } from '../../../@resources/anim/SebBuild';
import { SebWithHover } from '../../../@resources/anim/SebCommon';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { CommandDefinition } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { Icon } from '../../../concepts/resources/Icon';
import { Cell, Colors } from '../../../ui';
import { Badge } from '../../../ui/Badge';
import { DropButton } from '../../../ui/DropButton';
import { BetterPopArrow } from '../../../ui/PopArrow';
import { Roundings, Shadows } from '../../../ui/Standards';
import { useResolve } from '../../Container';
import { useFeatureCheck } from '../../common/FeatureElements';
import { Copy, DisableFrame, Flexer } from '../../dashboard/elements/LayoutElements';
import { FeatureManager } from '../../packages/feature/FeatureManager';
import { cameraSetStart } from '../commands/CameraCommands';
import { lockSetAll, lockSetLayout } from '../commands/ComponentCommands';
import { Popper, PopperButtonProps, PopperContentProps } from '../ui/Popper';
import { boardPublish } from './DesignModeCommands';
import { EditorModeWidgetButton, EditorModeWidgetContent, EditorModeWidgetFrame } from './EditorModeWidgetElements';

const Frame = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: .5rem;
    transform: translateY(.75rem);
`;

const LongButton = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: .5rem;
    padding: .375rem 1rem;
    font-weight: 420;
    color: ${p => p.color};
    border: solid .125rem ${p => p.color};
    box-shadow: ${Shadows.sm};
    background: white;
    border-radius: ${Roundings.lg};
    cursor: pointer;

    &:hover {
        color: ${p => darken(.1, p.color)};
        border: solid .125rem ${p => darken(.05, p.color)};
        background: #fdfdfd;
    }

    &:active {
        color: ${p => darken(.2, p.color)};
        border: solid .125rem ${p => darken(.1, p.color)};
        background: #fbfbfb;
    }
`;

export const DesignModeWidgetX = () => {
    return <Frame>
        <Flexer>
            <SebWithHover seb={SebBuild} />
        </Flexer>
        <LongButton color={Colors.primary}>
            <span>{'You are in Design Mode!'}</span>
            <Icon size=".875rem" src="deqo://icons/hero/chevron-up" />
        </LongButton>
    </Frame>;
};

const SelectIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    border-radius: .5rem;
    width: 3rem;
    height: 3rem;
    background: whitesmoke;
    border: solid .0625rem ${Colors.border};

    svg {
        flex: 0 0 auto;
        color: ${Colors.primary};
        width: 2rem;
        height: 2rem;
    }
`;

const SelectButton = styled(DropButton)`
    justify-content: start;
    gap: 1rem;
    ${Copy} {
        white-space: normal;
        font-weight: 450 !important;
        margin: 0 .5rem 0 0 !important;
    }

    &:hover {
        ${SelectIcon} {
            background: none;
            border: solid .0625rem transparent;
        }
    }
`;

const SelectGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    grid-gap: 1rem;
`;

const PremiumFrame = styled.div`
    position: relative;

    ${Badge} {
        position: absolute;
        background: ${Colors.positive};
        top: 0;
        right: 0;
    }
`;

const Premium: LFC<{ feature: string }> = ({ feature, children }) => {

    const has = useFeatureCheck(feature);
    if (!!has) return <>{children}</>;

    return <PremiumFrame>
        <Badge>Premium</Badge>
        <DisableFrame disabled>
            {children}
        </DisableFrame>
    </ PremiumFrame>;
};

const LockUnlockButton = ({ onSelect }: { onSelect: any }) => {

    const objects = useResolve(ObjectManager);
    const anyLocked = !!objects.find(x => x.has('locked') && !!x.get('locked'));

    if (anyLocked) {
        return <SelectButton padding=".5" onClick={() => onSelect(lockSetAll, [false])}>
            <SelectIcon>
                <Icon src="deqo://icons/deqo/design-unlock-all" />
            </SelectIcon>
            <Copy color={Colors.textLight}>Unlock everything</Copy>
        </SelectButton>;
    }
    else {
        return <SelectButton padding=".5" onClick={() => onSelect(lockSetAll, [true])}>
            <SelectIcon>
                <Icon src="deqo://icons/deqo/design-lock-all" />
            </SelectIcon>
            <Copy color={Colors.textLight}>Lock everything</Copy>
        </SelectButton>;
    }

};

const PopperButton = ({ active, onClick }: PopperButtonProps) => {
    return <EditorModeWidgetButton color={Colors.primary} onClick={onClick}>
        <span>{'You\'re in Design Mode!'}</span>
        <Icon size=".875rem" src="deqo://icons/hero/chevron-up" />
    </EditorModeWidgetButton>;
};

const PopperContent = ({ onClose }: PopperContentProps) => {
    const dispatch = useResolve(CommandDispatcher);

    const onSelect = useCallback((command: CommandDefinition<any>, args?: any[]) => {
        dispatch(command, args ?? []);
        onClose();
    }, [onClose]);

    const onPublish = useCallback(() => {
        dispatch(boardPublish);
        onClose();
    }, [onClose]);

    return <EditorModeWidgetContent>
        <Flexer padding="1" width="34">
            <SelectGrid>
                <Cell id="lock-layout">
                    <SelectButton padding=".5" onClick={() => onSelect(lockSetLayout, [true])}>
                        <SelectIcon>
                            <Icon src="deqo://icons/deqo/design-lock-layout" />
                        </SelectIcon>
                        <Copy color={Colors.textLight}>Lock layout</Copy>
                    </SelectButton>
                </Cell>
                <Cell>
                    <Premium feature="workspaces">
                        <SelectButton padding=".5" onClick={onPublish}>
                            <SelectIcon>
                                <Icon src="deqo://icons/deqo/design-publish" />
                            </SelectIcon>
                            <Copy color={Colors.textLight}>Publish as template</Copy>
                        </SelectButton>
                    </Premium>
                </Cell>
                <Cell>
                    <LockUnlockButton onSelect={onSelect} />
                </Cell>
                <Cell>
                    <Premium feature="workspaces">
                        <SelectButton padding=".5" onClick={() => onSelect(cameraSetStart)}>
                            <SelectIcon>
                                <Icon src="deqo://icons/deqo/design-starting" />
                            </SelectIcon>
                            <Copy color={Colors.textLight}>Set starting position</Copy>
                        </SelectButton>
                    </Premium>
                </Cell>
            </SelectGrid>
        </Flexer>
        <BetterPopArrow placement="top" color="#fff" border={[2, Colors.border]} size="1.5" style={{ left: '50%', marginTop: '.125rem' }} />
    </EditorModeWidgetContent>;
};

export const DesignModeWidget = () => {
    const features = useObserver(useResolve(FeatureManager));
    if (features.get('Hands')) return null;

    return <EditorModeWidgetFrame>
        <SebWithHover seb={SebBuild} />
        <Popper
            placement="top"
            button={PopperButton}
            content={PopperContent}
            inner={{ direction: 'up', offset: '1' }}
        />
    </EditorModeWidgetFrame>;
};
